import React, { useCallback, memo, useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, X, Camera, Sparkles, Loader2 } from 'lucide-react';
import Button from '../shared/Button';
import Input from '../shared/Input';
import SearchSelect from '../shared/SearchSelect';
import ImageBrowser from '../shared/ImageBrowser';
import ModalShell from '../shared/ModalShell';
import PaymentScheduleRulesEditor from './PaymentScheduleRulesEditor';
import { vendorServiceApi } from '../../services/vendorServiceService';
import { vendorSubscriptionKeys } from '../../services/subscriptionService';

// Individual array item component to prevent re-renders
const ArrayItem = memo(({ item, index, field, placeholder, onChange, onRemove }) => (
  <div className="flex items-center space-x-2">
    <Input
      value={item}
      onChange={(e) => onChange(field, index, e.target.value)}
      placeholder={placeholder}
      className="flex-1"
    />
    <button
      type="button"
      onClick={() => onRemove(field, index)}
      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40 !rounded-xl transition-colors"
      title="Remove item"
    >
      <X className="w-4 h-4" />
    </button>
  </div>
));

// Dynamic array field component - moved outside to prevent recreation
const DynamicArrayField = ({ 
  label, 
  field, 
  placeholder, 
  description, 
  items = [], 
  onAdd, 
  onRemove, 
  onChange 
}) => {
  // Ensure items is always an array
  const safeItems = Array.isArray(items) ? items : [];
  
  return (
    <div className="sm:col-span-2">
      {label ? (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label}
        </label>
      ) : null}
      <div className="space-y-2">
        {safeItems.map((item, index) => (
          <ArrayItem
            key={`${field}-${index}`}
            item={item}
            index={index}
            field={field}
            placeholder={placeholder}
            onChange={onChange}
            onRemove={onRemove}
          />
        ))}
        <button
          type="button"
          onClick={() => onAdd(field)}
          className="flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium py-2 px-3 border border-primary-300 dark:border-primary-700 !rounded-xl hover:bg-primary-50 dark:hover:bg-primary-950/40 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Add {label ? label.toLowerCase().split(' ')[0] : 'item'}</span>
        </button>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        {description}
      </p>
    </div>
  );
};

// Memoize the DynamicArrayField to prevent unnecessary re-renders
const MemoizedDynamicArrayField = memo(DynamicArrayField);

const ServicePackageModal = ({
  isOpen,
  isEdit,
  formData,
  services,
  selectedService,
  onClose,
  onSubmit,
  onInputChange,
  onArrayFieldChange,
  onAddArrayItem,
  onRemoveArrayItem,
  onImageSelect,
  onPaymentScheduleRulesChange,
  isLoading,
  maxPackagesPerService = null,
  existingPackages = [],
  aiAccess = { enabled: false, limit: null, used: 0 },
}) => {
  const queryClient = useQueryClient();
  const [showImageBrowser, setShowImageBrowser] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sampleGenerating, setSampleGenerating] = useState(false);
  const [aiError, setAiError] = useState('');
  const [sampleError, setSampleError] = useState('');
  const [usedOverride, setUsedOverride] = useState(null);

  const packageCountForSelectedService = useMemo(() => {
    if (!formData.service_id || !Array.isArray(existingPackages)) return 0;
    return existingPackages.filter((p) => p.service_id === formData.service_id).length;
  }, [formData.service_id, existingPackages]);

  const createBlockedByLimit =
    !isEdit &&
    maxPackagesPerService != null &&
    formData.service_id &&
    packageCountForSelectedService >= maxPackagesPerService;
  
  // Memoized handlers to prevent unnecessary re-renders
  const handleArrayItemChange = useCallback((field, index, value) => {
    onArrayFieldChange(field, index, value);
  }, [onArrayFieldChange]);

  const handleImageSelect = useCallback((image) => {
    if (image && onImageSelect) {
      onImageSelect(image);
      setShowImageBrowser(false);
    }
  }, [onImageSelect]);

  const handleAddArrayItem = useCallback((field) => {
    onAddArrayItem(field);
  }, [onAddArrayItem]);

  const handleRemoveArrayItem = useCallback((field, index) => {
    onRemoveArrayItem(field, index);
  }, [onRemoveArrayItem]);

  const handleClose = useCallback(() => {
    if (!isLoading && !showImageBrowser) onClose();
  }, [isLoading, showImageBrowser, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    setAiError('');
    setSampleError('');
    setUsedOverride(null);
  }, [isOpen]);

  const writesUsed = usedOverride ?? aiAccess.used ?? 0;
  const writeLimit = aiAccess.enabled ? aiAccess.limit : null;
  const writesLeft =
    writeLimit == null ? null : Math.max(0, Number(writeLimit) - Number(writesUsed));
  const atWriteLimit = aiAccess.enabled && writeLimit != null && writesLeft <= 0;

  const filledList = (items) =>
    (Array.isArray(items) ? items : []).map((item) => String(item).trim()).filter(Boolean);

  const handleGenerateDescription = async () => {
    const packageName = (formData.package_name || '').trim();
    if (!packageName) {
      setAiError('Enter a package name first so the writer knows what this package is.');
      return;
    }

    const service =
      (services || []).find((item) => String(item.id) === String(formData.service_id)) ||
      selectedService;

    setAiError('');
    setGenerating(true);
    try {
      const result = await vendorServiceApi.generateDescription({
        kind: 'package',
        package_name: packageName,
        service_name: service?.service_name || '',
        includes: filledList(formData.includes_items),
        features: filledList(formData.package_features),
        addons: filledList(formData.addon_options),
        draft: formData.description || '',
        base_price: formData.base_price === '' || formData.base_price == null ? null : formData.base_price,
      });
      const description = result?.data?.description;
      if (!description) {
        setAiError('AI writing returned an empty description. Try again.');
        return;
      }
      onInputChange('description', description);
      if (typeof result?.data?.used === 'number') {
        setUsedOverride(result.data.used);
      }
      queryClient.invalidateQueries({ queryKey: vendorSubscriptionKeys.all });
    } catch (error) {
      setAiError(
        error?.response?.data?.message ||
          'AI writing is unavailable right now. You can still type the description yourself.'
      );
    } finally {
      setGenerating(false);
    }
  };

  const mergeSamples = (current, samples) => {
    const kept = filledList(current);
    const additions = (Array.isArray(samples) ? samples : [])
      .map((item) => String(item).trim())
      .filter(
        (item) => item && !kept.some((existing) => existing.toLowerCase() === item.toLowerCase())
      );
    return [...kept, ...additions];
  };

  const handleGenerateSamples = async () => {
    const packageName = (formData.package_name || '').trim();
    if (!packageName) {
      setSampleError('Enter a package name first so the samples match this package.');
      return;
    }

    const service =
      (services || []).find((item) => String(item.id) === String(formData.service_id)) ||
      selectedService;

    setSampleError('');
    setSampleGenerating(true);
    try {
      const result = await vendorServiceApi.generatePackageSamples({
        package_name: packageName,
        service_name: service?.service_name || '',
        description: formData.description || '',
        includes: filledList(formData.includes_items),
        features: filledList(formData.package_features),
        addons: filledList(formData.addon_options),
        base_price: formData.base_price === '' || formData.base_price == null ? null : formData.base_price,
      });
      const data = result?.data || {};
      onInputChange('includes_items', mergeSamples(formData.includes_items, data.includes));
      onInputChange('package_features', mergeSamples(formData.package_features, data.features));
      onInputChange('addon_options', mergeSamples(formData.addon_options, data.addons));
      if (typeof data.used === 'number') {
        setUsedOverride(data.used);
      }
      queryClient.invalidateQueries({ queryKey: vendorSubscriptionKeys.all });
    } catch (error) {
      setSampleError(
        error?.response?.data?.message ||
          'Sample writing is unavailable right now. You can still add items yourself.'
      );
    } finally {
      setSampleGenerating(false);
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      labelledBy="package-modal-title"
      panelClassName="max-w-3xl"
    >
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 max-h-[min(90vh,900px)] overflow-hidden flex flex-col">
        {/* Fixed Header */}
        <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <h3 id="package-modal-title" className="text-lg font-semibold text-gray-900 dark:text-gray-50">
            {isEdit ? 'Edit Package' : 'Create New Package'}
          </h3>
          
          {selectedService && (
            <div className="mt-3 p-3 bg-sky-50 dark:bg-sky-950/40 border border-sky-200 dark:border-sky-800/50 rounded-xl">
              <p className="text-sm text-sky-800 dark:text-sky-300">
                <span className="font-medium">Creating package for:</span> {selectedService.service_name}
              </p>
            </div>
          )}
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto overflow-x-hidden" style={{ flex: '1 1 0%', minHeight: 0 }}>
          <div className="p-4 sm:p-6">
            <form id="package-form" onSubmit={onSubmit} className="space-y-6">
            {/* Basic Information Section */}
            <div className="bg-gray-50 dark:bg-gray-950/50 p-4 rounded-xl">
              <h4 className="text-md font-semibold text-gray-900 dark:text-gray-50 mb-4 flex items-center">
                <span className="w-2 h-2 bg-primary-600 rounded-full mr-3"></span>
                Basic Information
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Package Name *
                  </label>
                  <Input
                    type="text"
                    value={formData.package_name}
                    onChange={(e) => onInputChange('package_name', e.target.value)}
                    placeholder="Enter package name"
                    required
                  />
                </div>

                {/* Package Image */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Package Image
                  </label>
                  <div className="flex items-center gap-4">
                    {formData.image_url ? (
                      <div className="relative">
                        <img
                          src={formData.image_url}
                          alt="Package"
                          className="w-24 h-24 rounded-xl object-cover border-2 border-gray-200 dark:border-gray-700"
                        />
                        <button
                          type="button"
                          onClick={() => onInputChange('image_url', '')}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-xl bg-gray-200 dark:bg-gray-800 flex items-center justify-center border-2 border-gray-300 dark:border-gray-600">
                        <Camera className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                    <Button
                      type="button"
                      onClick={() => setShowImageBrowser(true)}
                      variant="outline"
                      size="sm"
                      icon={Camera}
                      className="!rounded-xl"
                    >
                      {formData.image_url ? 'Change Image' : 'Select Image'}
                    </Button>
                  </div>
                </div>
                
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Service *
                  </label>
                  <SearchSelect
                    value={formData.service_id}
                    onChange={(value) => onInputChange('service_id', value)}
                    options={services.map(service => ({
                      value: service.id,
                      label: service.service_name
                    }))}
                    placeholder="Select a service"
                    required
                    disabled={!!selectedService}
                  />
                  {!isEdit && formData.service_id && maxPackagesPerService != null && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Packages on this service: {packageCountForSelectedService} / {maxPackagesPerService}
                    </p>
                  )}
                  {createBlockedByLimit && (
                    <div className="mt-3 rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-sm text-amber-950 dark:text-amber-200">
                      You&apos;ve reached the maximum packages allowed per service on your current plan.{' '}
                      <Link to="/vendor/subscription" className="font-medium underline hover:text-amber-900 dark:hover:text-amber-100">
                        Upgrade your plan
                      </Link>{' '}
                      to add more packages to this service.
                    </div>
                  )}
                </div>
                
                <div className="sm:col-span-2">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Description *
                    </label>
                    {aiAccess.enabled ? (
                      <button
                        type="button"
                        onClick={handleGenerateDescription}
                        disabled={generating || sampleGenerating || atWriteLimit}
                        className="inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-800 hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-primary-800 dark:bg-primary-950/40 dark:text-primary-200 dark:hover:bg-primary-950/70"
                      >
                        {generating ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Sparkles className="h-3.5 w-3.5" />
                        )}
                        {generating
                          ? 'Writing…'
                          : formData.description?.trim()
                            ? 'Improve with AI'
                            : 'Write with AI'}
                      </button>
                    ) : null}
                  </div>
                  <textarea
                    value={formData.description}
                    onChange={(e) => onInputChange('description', e.target.value)}
                    placeholder="Describe your package in detail"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                  {aiAccess.enabled ? (
                    <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                      {atWriteLimit
                        ? 'You have used every AI write on this plan for the month.'
                        : writeLimit == null
                          ? 'Uses the package name, service, price, included items, features, and add-ons. Unlimited on this plan.'
                          : `${writesLeft} of ${writeLimit} AI ${writeLimit === 1 ? 'write' : 'writes'} left this month. Uses the package name, service, price, included items, features, and add-ons.`}
                    </p>
                  ) : (
                    <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                      AI writing is not on your current plan.{' '}
                      <Link
                        to="/vendor/subscription"
                        className="text-primary-600 hover:text-primary-700 dark:text-primary-400 underline"
                        onClick={onClose}
                      >
                        See plans
                      </Link>
                    </p>
                  )}
                  {aiError ? (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{aiError}</p>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Pricing Section */}
            <div className="bg-gray-50 dark:bg-gray-950/50 p-4 rounded-xl">
              <h4 className="text-md font-semibold text-gray-900 dark:text-gray-50 mb-4 flex items-center">
                <span className="w-2 h-2 bg-green-600 rounded-full mr-3"></span>
                Pricing
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Base Price ($) *
                  </label>
                  <Input
                    type="number"
                    value={formData.base_price}
                    onChange={(e) => onInputChange('base_price', e.target.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    required
                    hideSpinner={true}
                    preventTextInput={true}
                    formatNumber={true}
                    decimalPlaces={2}
                  />
                </div>
              </div>
            </div>

            {/* Package Details Section */}
            <div className="bg-gray-50 dark:bg-gray-950/50 p-4 rounded-xl">
              <div className="flex items-start justify-between gap-3 mb-6">
                <div>
                  <h4 className="text-md font-semibold text-gray-900 dark:text-gray-50 flex items-center">
                    <span className="w-2 h-2 bg-sky-600 rounded-full mr-3"></span>
                    Package Details
                  </h4>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {aiAccess.enabled
                      ? 'Generate a starter list, then keep, edit, or remove any line.'
                      : 'AI samples are not on your current plan.'}
                  </p>
                </div>
                {aiAccess.enabled ? (
                  <button
                    type="button"
                    onClick={handleGenerateSamples}
                    disabled={sampleGenerating || generating || atWriteLimit}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-800 hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-primary-800 dark:bg-primary-950/40 dark:text-primary-200 dark:hover:bg-primary-950/70"
                  >
                    {sampleGenerating ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Sparkles className="h-3.5 w-3.5" />
                    )}
                    {sampleGenerating ? 'Writing samples…' : 'Generate samples'}
                  </button>
                ) : (
                  <Link
                    to="/vendor/subscription"
                    className="shrink-0 text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 underline"
                    onClick={onClose}
                  >
                    See plans
                  </Link>
                )}
              </div>
              {sampleError ? (
                <p className="mb-4 text-xs text-red-600 dark:text-red-400">{sampleError}</p>
              ) : null}
              
              {/* What's Included Subsection */}
              <div className="mb-6">
                <h5 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                  <span className="w-1.5 h-1.5 bg-sky-500 rounded-full mr-2"></span>
                  What's Included
                </h5>
                <div className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                  <MemoizedDynamicArrayField
                    label=""
                    field="includes_items"
                    placeholder="e.g., Photo booth, Basic props, Digital gallery"
                    description="List all items and services included in this package"
                    items={formData.includes_items}
                    onAdd={handleAddArrayItem}
                    onRemove={handleRemoveArrayItem}
                    onChange={handleArrayItemChange}
                  />
                </div>
              </div>

              {/* Package Features Subsection */}
              <div className="mb-6">
                <h5 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></span>
                  Package Features
                </h5>
                <div className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                  <MemoizedDynamicArrayField
                    label=""
                    field="package_features"
                    placeholder="e.g., Instant prints, Digital copies, Unlimited photos"
                    description="Highlight key features that make this package special"
                    items={formData.package_features}
                    onAdd={handleAddArrayItem}
                    onRemove={handleRemoveArrayItem}
                    onChange={handleArrayItemChange}
                  />
                </div>
              </div>

              {/* Add-on Options Subsection */}
              <div>
                <h5 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></span>
                  Add-on Options
                </h5>
                <div className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                  <MemoizedDynamicArrayField
                    label=""
                    field="addon_options"
                    placeholder="e.g., Extra hour (+$75), Premium props (+$50)"
                    description="List additional services customers can purchase with this package"
                    items={formData.addon_options}
                    onAdd={handleAddArrayItem}
                    onRemove={handleRemoveArrayItem}
                    onChange={handleArrayItemChange}
                  />
                </div>
              </div>
            </div>

            {/* Payment Schedule Section */}
            <div className="bg-gray-50 dark:bg-gray-950/50 p-4 rounded-xl">
              <h4 className="text-md font-semibold text-gray-900 dark:text-gray-50 mb-2 flex items-center">
                <span className="w-2 h-2 bg-amber-500 rounded-full mr-3"></span>
                Payment Schedule
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                Clients see installments based on how far their event date is from today. Amounts update when they pick a date.
              </p>
              <PaymentScheduleRulesEditor
                value={formData.payment_schedule_rules}
                onChange={onPaymentScheduleRulesChange}
                samplePrice={parseFloat(formData.base_price) || 370}
              />
            </div>

            {/* Settings Section */}
            <div className="bg-gray-50 dark:bg-gray-950/50 p-4 rounded-xl">
              <h4 className="text-md font-semibold text-gray-900 dark:text-gray-50 mb-4 flex items-center">
                <span className="w-2 h-2 bg-primary-600 rounded-full mr-3"></span>
                Settings
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_popular}
                      onChange={(e) => onInputChange('is_popular', e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Popular Package</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Popular packages are highlighted to customers
                  </p>
                </div> */}
                
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => onInputChange('is_active', e.target.checked)}
                      className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Active Package</span>
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Only active packages are visible to customers
                  </p>
                </div>
              </div>
            </div>
            </form>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="flex-shrink-0 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/50">
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <Button
              type="button"
              onClick={handleClose}
              variant="outline"
              className="w-full sm:w-auto !rounded-xl"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="package-form"
              disabled={isLoading || createBlockedByLimit}
              loading={isLoading}
              className="w-full sm:w-auto !rounded-xl"
            >
              {isEdit ? 'Update Package' : 'Create Package'}
            </Button>
          </div>
        </div>

        {/* Nested image browser overlay inside panel */}
        {showImageBrowser && (
          <div className="absolute inset-0 z-20 flex flex-col bg-white dark:bg-gray-900 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800 shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Select Package Image</h3>
              <button
                type="button"
                onClick={() => setShowImageBrowser(false)}
                className="p-2 !rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200"
                aria-label="Close image browser"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-4 sm:p-6">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                General uploads only — portfolio images are not listed here. New uploads are saved as general images.
              </p>
              <ImageBrowser
                listQueryParams={{ non_portfolio: true }}
                uploadImageType="general"
                showUpload
                showDelete
                showSetDefault={false}
                onSelectImage={handleImageSelect}
              />
            </div>
          </div>
        )}
      </div>
    </ModalShell>
  );
};

export default ServicePackageModal;
